import { useMemo } from 'react'
import { useIsSanofiOrganization } from '../useIsSanofiOrganization'
import { useIsOtsukaOrganization } from '../useIsOtsukaOrganization'
import { useIsNNOrganization } from '../useIsNNOrganization'
import { ACTIVITY_STATUSES } from './useActivityStatuses.constants'
import { ActivityStatusDetails } from './useActivityStatuses.types'
import { getActivityStatusDetailsIcon, normalizeActivityStatus } from './useActivityStatuses.helpers'
import { useTheme } from '@mui/styles'

const UNCATEGORIZED_STATUS_ID = 'Uncategorized'

const useActivityStatuses = () => {
    const isSanofiOrganization = useIsSanofiOrganization()
    const isOtsukaOrganization = useIsOtsukaOrganization()
    const isNNOrganization = useIsNNOrganization()

    const theme = useTheme()

    const activityStatusDetails = useMemo((): ActivityStatusDetails[] => {
        let activityStatusConfig = ACTIVITY_STATUSES.COMMON

        if (isSanofiOrganization) {
            activityStatusConfig = ACTIVITY_STATUSES.SANOFI
        }

        if (isOtsukaOrganization) {
            activityStatusConfig = ACTIVITY_STATUSES.OTSUKA
        }

        if (isNNOrganization) {
            activityStatusConfig = ACTIVITY_STATUSES.NN
        }

        return activityStatusConfig
            .sort((a, b) => a.order - b.order)
            .map((detail) => ({
                id: detail.id,
                name: detail.name,
                color: detail.color,
                icon: getActivityStatusDetailsIcon(detail),
                order: detail.order,
            }))
    }, [isSanofiOrganization, isOtsukaOrganization, isNNOrganization])

    const activityStatuses = useMemo((): string[] => {
        return activityStatusDetails.map((statusDetails) => statusDetails.id)
    }, [activityStatusDetails])

    const getActivityStatusDetails = (activityStatus: string): ActivityStatusDetails => {
        const activityStatusId = normalizeActivityStatus(activityStatus) || UNCATEGORIZED_STATUS_ID

        const statusDetails = activityStatusDetails.find((status) => status.id === activityStatusId)

        if (!statusDetails) {
            return {
                id: activityStatusId,
                name: activityStatusId,
                color: theme.palette.figma.grayscale.textHier2,
                icon: getActivityStatusDetailsIcon({ shape: null, color: theme.palette.figma.grayscale.textHier2 }),
                order: Number.MAX_SAFE_INTEGER,
            }
        }

        return statusDetails
    }

    return {
        activityStatuses,
        getActivityStatusDetails,
        activityStatusDetails,
        isSanofiOrganization,
        isOtsukaOrganization,
        isNNOrganization,
    }
}

export default useActivityStatuses
